import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Anchor, Sun, Music, MapPin, Clock, ChevronRight, Star, ArrowRight, Phone, Check, Crown, Users, Ticket, GlassWater, Wine, UtensilsCrossed } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import { CONTACT } from "../data/cruiseData";
import useTicketPrices from "../hooks/useTicketPrices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────────────────────────────────────────────────────────────────────────
   AUSTRALIA DAY CRUISES SYDNEY HARBOUR, Premium ticketed page
   Theme: warm Aussie gold + sunburst amber + harbour blue
   Two sessions: Day (10:45am-2:45pm, $145/$70) · Night & Fireworks (6:30pm-10:30pm, $145/$70)
   ─────────────────────────────────────────────────────────────────────────── */

const GOLD = "#E8B53A";
const SUNSET = "#FFCC70";
const HOT = "#F59E0B";

const HIGHLIGHTS = [
 { icon: Sparkles, label: "AUS DAY FIREWORKS", sub: "Night session prime view" },
 { icon: Anchor, label: "REGATTA & FERRYTHON", sub: "Day session front row" },
 { icon: GlassWater,label: "LICENSED BAR", sub: "Beer, wine, spirits" },
 { icon: Music, label: "AUSSIE LEGENDS", sub: "Opera House forecourt" }];

const TIMELINE_DAY = [
 { time: "10:45 AM", title: "Boarding · King St Wharf 6", body: "Welcome aboard Magic with cold drinks and harbour breeze." },
 { time: "11:30 AM", title: "Sydney Harbour Regatta", body: "Front-row to the historic tall ships, ferries and yacht parade." },
 { time: "12:30 PM", title: "Standing canapés + mini buffet", body: "Spring rolls, party pies, smoked ham, fresh salads, artisan rolls." },
 { time: "1:30 PM", title: "Ferrython race", body: "Watch Sydney's iconic green and yellow ferries battle it out." },
 { time: "2:45 PM", title: "Return to wharf", body: "Disembark King St Wharf 6 after 4 hours on the harbour." }];

const TIMELINE_NIGHT = [
 { time: "6:30 PM", title: "Boarding · King St Wharf 6", body: "Step aboard as the sun sets over Darling Harbour." },
 { time: "7:00 PM", title: "Aussie musical legends · Opera House Forecourt", body: "Cruise past the live stage on the harbour." },
 { time: "7:45 PM", title: "Standing canapés + mini buffet", body: "Hot canapés, smoked ham, salads. Bar open all night." },
 { time: "8:45 PM", title: "Jet skis · fly-boards · dancing yachts", body: "Sydney's maritime cavalcade on full display." },
 { time: "9:00 PM", title: "AUSTRALIA DAY FIREWORKS", body: "Dazzling display lighting up the night sky. Best seat on the water." },
 { time: "10:30 PM", title: "Return to wharf", body: "Cruise winds down · disembark at King St Wharf 6." }];

const PACKAGES = [
 {
  tier: "DAY SESSION",
  eventKey: "aus-day-2026-day",
  badge: "REGATTA + FERRYTHON",
  adult: "$145",
  child: "$70",
  caption: "Per person · 4 hours · 10:45am, 2:45pm",
  Icon: Sun,
  inclusions: [
   "4 hours cruising on Magic", "Front-row Sydney Harbour Regatta", "Sydney ferry race · Ferrython", "Canapés on arrival + standing mini buffet (included)", "Unlimited soft drinks (included)", "Beer, wine, spirits · available to purchase at the licensed bar (extra)", "King St Wharf 6 · boarding 10:45am"],
  cta: "Buy Day Tickets",
  highlight: false,
 },
 {
  tier: "NIGHT SESSION",
  eventKey: "aus-day-2026-night",
  badge: "FIREWORKS · MOST POPULAR",
  adult: "$145",
  child: "$70",
  caption: "Per person · 4 hours · 6:30pm, 10:30pm",
  Icon: Sparkles,
  inclusions: [
   "4 hours cruising on Magic", "Australia Day fireworks · prime viewing", "Aussie musical legends · live from Opera House stage", "Canapés on arrival + standing mini buffet (included)", "Unlimited soft drinks (included)", "Beer, wine, spirits · available to purchase at the licensed bar (extra)", "King St Wharf 6 · boarding 6:30pm"],
  cta: "Buy Night Tickets",
  highlight: true,
 }];

const MENU_BLOCKS = [
 {
  label: "Canapés on arrival",
  badge: "INCLUDED",
  badgeIncluded: true,
  Icon: Sparkles,
  caption: "Served fresh as you board.",
  items: [
   "Vegetarian spring rolls · oven baked · served with sweet chilli sauce (hot, veg)", "Gourmet party pies · selection of beef burgundy and chicken & leek (hot)"],
 },
 {
  label: "From the mini buffet",
  badge: "INCLUDED",
  badgeIncluded: true,
  Icon: UtensilsCrossed,
  caption: "Standing buffet · plenty for everyone.",
  items: [
   "Gourmet smoked ham with assorted condiments", "Classic potato salad · sour cream and chive, topped with crispy streaky bacon bits", "Fresh garden salad · mixed leaf greens, French dressing, red capsicum, shredded carrot, Spanish onion, tomato wedges", "Selection of artisan bakery rolls with individual butter portions"],
 },
 {
  label: "Beverage",
  badge: "SOFT DRINKS INCLUDED · ALCOHOL EXTRA",
  badgeIncluded: false,
  Icon: GlassWater,
  caption: "Soft drinks free · alcohol pay-as-you-go.",
  items: [
   "Unlimited soft drinks (Coca-Cola range, juices, water), INCLUDED in your ticket", "Beer, wine, spirits and cocktails, available to PURCHASE at the licensed bar", "Cash and card both accepted at the bar", "No BYO alcohol, fully licensed venue"],
 }];

const REVIEWS = [
 { name: "Hannah G.", role: "Aus Day Night 2024", rating: 5, body: "Best seats for the fireworks · we'll never watch from the shore again. Bar staff were sensational." },
 { name: "Michael T.", role: "Day Cruise 2024", rating: 5, body: "Took the family. Kids loved the ferries, parents loved the harbour breeze and cold beers. Worth every cent." },
 { name: "Olivia K.", role: "Aus Day Night 2026", rating: 5, body: "Booked for our anniversary. The fireworks from the water are unreal. Food was generous and the crew was so warm." }];

const FAQ = [
 { q: "What time should I arrive?", a: "Arrive 15 minutes before boarding. Day session boards at 10:30am for 10:45am departure; night session boards at 6:15pm for 6:30pm departure. King St Wharf 6, Darling Harbour." },
 { q: "Which session has the fireworks?", a: "The night session (6:30pm, 10:30pm) includes the Australia Day fireworks at approximately 9pm. The day session covers the Regatta and Ferrython instead." },
 { q: "What's the dress code?", a: "Smart-casual. Aussie themed if you like, most guests rock thongs and stubbies in the day, smart shirts and dresses for the night session." },
 { q: "What food and drinks are included?", a: "Canapés on arrival (vegetarian spring rolls + gourmet party pies) and a standing mini buffet (gourmet smoked ham, classic potato salad, fresh garden salad, artisan bakery rolls), both INCLUDED. Unlimited soft drinks (Coca-Cola range, juices, water), INCLUDED. Beer, wine, spirits and cocktails are available to PURCHASE at the licensed bar (not included)." },
 { q: "Are drinks at the bar free?", a: "Only soft drinks are included (unlimited). All alcoholic drinks, beer, wine, spirits and cocktails, are pay-as-you-go at the licensed bar. Cash and card both accepted. No BYO alcohol permitted." },
 { q: "What if it rains?", a: "We cruise rain or shine, the Magic has a fully covered indoor area and the fireworks proceed in any conditions." }];

export default function AustraliaDayCruise() {
 const [avail, setAvail] = useState({});
 const { priceFor } = useTicketPrices({
  "aus-day-2026-day": { adult: 145, child: 70 },
  "aus-day-2026-night": { adult: 145, child: 70 },
 });
 // CMS-driven prices override the static PACKAGES adult/child strings
 const livePackages = useMemo(
  () => PACKAGES.map((p) => ({
   ...p,
   adult: priceFor(p.eventKey, "adult") || p.adult,
   child: priceFor(p.eventKey, "child") || p.child,
  })),
  [priceFor],
 );
 useEffect(() => { document.title = "Australia Day Cruises Sydney Harbour 2027, Magic Cruises"; }, []);
 useEffect(() => {
  (async () => {
   try { const r = await axios.get(`${API}/events/availability`); setAvail(r.data.events || {}); } catch (e) { /* ignore */ }
  })();
 }, []);

 return (
  <main className="bg-[#0A1417] text-white overflow-hidden" data-testid="aus-day-page">
   <SEOHead
    title="Australia Day Cruises Sydney Harbour 2027, Day & Fireworks Tickets | Magic Cruises"
    description="Australia Day 26 Jan 2027 on Sydney Harbour aboard Magic. Day session 10:45am ($145/$70) with Regatta & Ferrython. Night session 6:30pm ($145/$70) with prime view of Australia Day fireworks. King St Wharf 6."
    image="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zd1xz4zn_AUSTRALIA-DAY-CRUISES-SYDNEY-HARBOUR.jpg"
    canonical="/cruises/australia-day-cruises-sydney-harbour"
    keywords="Australia Day cruise Sydney, Australia Day 2027 cruise tickets, Sydney Harbour Regatta cruise, Ferrython cruise tickets, Australia Day fireworks cruise"
    includeLocalBusiness
    breadcrumbs={[
     { name: "Home", url: "/" },
     { name: "Ticketed Cruises", url: "/ticketed-cruises" },
     { name: "Australia Day Cruises Sydney Harbour", url: "/cruises/australia-day-cruises-sydney-harbour" },
    ]}
    eventSchema={{
     name: "Australia Day 2027 Cruise · MV Sydney Magic",
     startDate: "2027-01-26T10:45:00+11:00",
     endDate: "2027-01-26T22:30:00+11:00",
     description: "Australia Day on Sydney Harbour aboard Magic , day session covers the Regatta and Ferrython; night session includes prime viewing of the Australia Day fireworks. Canapés, mini buffet, unlimited soft drinks, licensed bar.",
     offers: { price: 145, availability: "https://schema.org/InStock" },
    }}
   />

   {/* ═══════════ HERO ═══════════ */}
   <section className="relative overflow-hidden bg-[#0E1B1F] flex flex-col isolate" data-testid="aus-day-hero">
    <div className="relative w-full min-h-[88vh] flex items-center justify-center px-5 sm:px-8 lg:px-10 pt-28 pb-16 lg:py-28">
     <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zd1xz4zn_AUSTRALIA-DAY-CRUISES-SYDNEY-HARBOUR.jpg" alt="Australia Day fireworks and harbour celebration on MV Sydney Magic , King St Wharf Sydney" className="absolute inset-0 w-full h-full object-cover opacity-60 scale-110" fetchPriority="high" />
     <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(14,27,31,0.85) 0%, rgba(22,41,47,0.55) 30%, rgba(10,20,23,0.55) 60%, rgba(8,16,18,0.95) 100%)" }} />
     <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-70" style={{ background: "radial-gradient(ellipse at center top, rgba(255,200,90,0.28) 0%, transparent 60%)" }} />
     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-50" style={{ background: "radial-gradient(ellipse at center bottom, rgba(232,181,58,0.25) 0%, transparent 65%)" }} />
     <Starfield density={50} color={SUNSET} glow="rgba(255,204,112,0.5)" fixed={false} intensity={0.55} />
     <div className="absolute top-[12%] right-[8%] w-[26rem] h-[26rem] rounded-full blur-[110px] pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(232,181,58,0.25)" }} />
     <div className="absolute bottom-[8%] left-[6%] w-[22rem] h-[22rem] rounded-full blur-[100px] pointer-events-none animate-pulse" style={{ animationDuration: "8s", animationDelay: "2s", backgroundColor: "rgba(255,150,80,0.18)" }} />

     <h1 className="sr-only">Australia Day Cruises Sydney Harbour 2027, Day Cruise + Night Fireworks Cruise aboard Magic</h1>

     <div className="absolute top-24 lg:top-28 right-5 lg:right-10 z-20 hidden lg:flex flex-col gap-2 items-end">
      {["26 Jan 2027", "King St Wharf 6", `Adult ${priceFor("aus-day-2026-day", "adult") || "$145"}`, "Two sessions"].map((b, i) => (
       <div key={b} className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-xl ring-1 text-white text-[10px] uppercase tracking-[0.28em] font-black animate-in slide-in-from-right fade-in duration-700" style={{ animationDelay: `${i * 120}ms`, borderColor: `${GOLD}55` }}>
        <span className="inline-flex items-center gap-2"><span className="w-1 h-1 rounded-full" style={{ backgroundColor: GOLD }} />{b}</span>
       </div>
      ))}
     </div>

     <div className="relative z-10 text-center max-w-5xl mx-auto w-full">
      <div className="inline-flex items-center gap-3 sm:gap-4 mb-5 lg:mb-7 animate-in fade-in slide-in-from-top-4 duration-1000">
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: GOLD, opacity: 0.7 }} />
       <span className="text-white/95 text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.5em] font-bold">Sydney Harbour · 26 January</span>
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: GOLD, opacity: 0.7 }} />
      </div>

      <h2 className="font-black tracking-tight leading-[0.88] animate-in fade-in zoom-in-95 duration-1000" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 11vw, 8.5rem)", letterSpacing: "-0.02em" }}>
       <span className="block bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, #FFFFFF 0%, ${SUNSET} 35%, ${GOLD} 70%, ${HOT} 100%)`, filter: "drop-shadow(0 4px 28px rgba(232,181,58,0.45))" }}>
        AUSTRALIA DAY
       </span>
       <em className="block italic font-light mt-2 lg:mt-3 text-white/90" style={{ fontSize: "clamp(1.6rem, 4vw, 3rem)" }}>on the harbour.</em>
      </h2>

      <p className="text-white/85 text-base sm:text-lg lg:text-xl mt-6 lg:mt-7 max-w-2xl mx-auto leading-[1.65] animate-in fade-in duration-1000" style={{ animationDelay: "300ms" }}>
       Two cruises. One unforgettable day. Day cruise for the Regatta + Ferrython. Night cruise for the fireworks. Aboard Magic.
      </p>

      <div className="mt-9 lg:mt-12 flex flex-wrap items-center justify-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-1000" style={{ animationDelay: "500ms" }}>
       <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-day" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${SUNSET} 0%, ${GOLD} 100%)`, boxShadow: "0 20px 50px -10px rgba(232,181,58,0.6)" }} data-testid="aus-day-hero-day-cta">
        <Sun className="w-4 h-4" /> Buy Day Tickets · {priceFor("aus-day-2026-day", "adult") || "$145"}
       </Link>
       <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-night" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-amber-300/70 hover:text-amber-300 transition-all text-white" data-testid="aus-day-hero-night-cta">
        <Sparkles className="w-4 h-4" /> Night & Fireworks · {priceFor("aus-day-2026-night", "adult") || "$145"}
       </Link>
      </div>

      <div className="mt-10 lg:mt-14 grid grid-cols-3 gap-4 lg:gap-10 max-w-3xl mx-auto animate-in fade-in duration-1000" style={{ animationDelay: "700ms" }}>
       {[
        { i: Anchor, label: ["King St Wharf 6", "Darling Harbour"] },
        { i: Clock, label: ["4 Hour", "Cruise"] },
        { i: Music, label: ["Live music", "+ fireworks"] }].map((c, i) => (
        <div key={i} className="flex flex-col items-center gap-2 lg:gap-3 text-center">
         <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ring-1 backdrop-blur" style={{ backgroundColor: "rgba(232,181,58,0.10)", borderColor: GOLD }}>
          <c.i className="w-4 h-4 lg:w-6 lg:h-6" style={{ color: GOLD }} strokeWidth={1.5} />
         </span>
         <div className="text-white text-[10px] sm:text-[11px] lg:text-sm uppercase tracking-[0.18em] lg:tracking-[0.22em] font-black leading-snug">
          {c.label.map((line, j) => <div key={j}>{line}</div>)}
         </div>
        </div>
       ))}
      </div>
     </div>
    </div>
   </section>

   {/* ═══════════ HIGHLIGHTS STRIP ═══════════ */}
   <section className="relative border-y border-amber-300/15 bg-gradient-to-r from-[#0E1B1F] via-[#142026] to-[#0E1B1F]" data-testid="aus-day-highlights">
    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(232,181,58,0.10) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(255,150,80,0.08) 0%, transparent 60%)" }} />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-10">
      {HIGHLIGHTS.map((h, i) => (
       <div key={i} className="flex items-center gap-4 lg:border-r lg:border-amber-300/15 lg:pr-6 last:border-0">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: "rgba(232,181,58,0.10)", borderColor: GOLD }}>
         <h.icon className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.5} />
        </div>
        <div>
         <div className="font-black text-white text-sm tracking-[0.18em]">{h.label}</div>
         <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold mt-1">{h.sub}</div>
        </div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ EXPERIENCE / INTRO ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0E1B1F] via-[#16292F] to-[#0E1B1F] py-24 lg:py-32 overflow-hidden isolate">
    <Starfield density={35} color={SUNSET} glow="rgba(255,204,112,0.45)" fixed={false} intensity={0.5} />
    <div className="absolute -top-32 left-1/3 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(232,181,58,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10 text-center">
     <GoldEyebrow>The Australia Day experience</GoldEyebrow>
     <h2 className="font-black tracking-tight text-white mt-6 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
      Celebrate the joy of <em className="font-light italic" style={{ color: GOLD }}>being Australian</em> on the water.
     </h2>
     <p className="text-white/82 text-base lg:text-lg leading-[1.85] mt-7 max-w-3xl mx-auto font-light">
      26th January in Sydney is the most magical day of the year on the harbour. Aboard Magic, you're in the centre of it all, front-row to the Sydney Harbour Regatta, the iconic Ferrython race, and at night, the Australia Day fireworks display. Aussie musical legends rock the Opera House Forecourt stage. Jet-skis, fly-boarders and dancing yachts perform between the boats. This is your best seat on the harbour.
     </p>
    </div>
   </section>

   {/* ═══════════ PACKAGES (Day + Night) ═══════════ */}
   <section id="packages" className="relative bg-gradient-to-b from-[#0E1B1F] via-[#16292F] to-[#0E1B1F] py-24 lg:py-32 overflow-hidden isolate border-y border-amber-300/10" data-testid="aus-day-packages">
    <Starfield density={30} color={SUNSET} glow="rgba(255,204,112,0.45)" fixed={false} intensity={0.45} />
    <div className="absolute -top-32 right-1/4 w-[34rem] h-[34rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(232,181,58,0.12)" }} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <GoldEyebrow>Two sessions · pick your day</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Day cruise or <em className="italic font-light" style={{ color: GOLD }}>night fireworks</em>?
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">Same price · same incredible food · totally different vibe. Buy tickets online instantly.</p>
     </div>
     <div className="grid lg:grid-cols-2 gap-7 lg:gap-9">
      {livePackages.map((p, i) => {
       const ev = avail[p.eventKey];
       const remaining = ev?.remaining;
       const soldOut = ev?.status === "sold_out";
       return (
        <div key={i} className={`relative rounded-3xl ring-1 p-8 lg:p-10 backdrop-blur-md ${p.highlight ? "lg:scale-105 z-10" : ""}`} style={{ background: p.highlight ? `linear-gradient(135deg, rgba(232,181,58,0.14), rgba(14,27,31,0.85) 60%, rgba(8,16,18,0.85))` : "rgba(20,32,38,0.55)", borderColor: p.highlight ? `${GOLD}66` : "rgba(255,255,255,0.12)", boxShadow: p.highlight ? "0 30px 80px -20px rgba(232,181,58,0.45)" : "0 30px 80px -20px rgba(0,0,0,0.4)" }}>
         <div className="flex items-center justify-between mb-6">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-black ${p.highlight ? "" : "ring-1 ring-white/25 bg-white/10 text-white"}`} style={p.highlight ? { backgroundColor: GOLD, color: "#0A0A0A" } : {}}>{p.badge}</span>
          <p.Icon className="w-7 h-7" style={{ color: p.highlight ? GOLD : "rgba(255,255,255,0.6)" }} strokeWidth={1.5} />
         </div>
         <div className="text-white/65 text-[11px] uppercase tracking-[0.32em] font-bold mb-2">{p.tier}</div>
         <div className="flex items-baseline gap-3 mb-1">
          <span className="text-5xl lg:text-6xl font-black text-white tabular-nums">{p.adult}</span>
          <span className="text-white/55 text-sm font-bold">adult</span>
          <span className="text-white/40 text-2xl">·</span>
          <span className="text-2xl font-black tabular-nums" style={{ color: GOLD }}>{p.child}</span>
          <span className="text-white/55 text-sm font-bold">child</span>
         </div>
         <div className="text-white/55 text-xs uppercase tracking-widest font-bold mb-5">{p.caption}</div>
         {remaining !== undefined && !soldOut && remaining <= 30 && (
          <div className="text-[11px] uppercase tracking-[0.22em] font-black mb-4 inline-flex items-center gap-1.5" style={{ color: HOT }}>
           <Sparkles className="w-3 h-3" /> Only {remaining} left · selling fast
          </div>
         )}
         <ul className="space-y-3 my-6 border-t border-white/10 pt-6">
          {p.inclusions.map((inc, j) => (
           <li key={j} className="flex items-start gap-3 text-white/85 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} strokeWidth={2.5} />
            <span>{inc}</span>
           </li>
          ))}
         </ul>
         {soldOut ? (
          <div className="mt-2 inline-flex items-center justify-center w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase bg-red-500/15 text-red-300 ring-1 ring-red-400/30" data-testid={`aus-day-pkg-soldout-${i}`}>SOLD OUT</div>
         ) : (
          <Link to={`/cruises/australia-day-cruises-sydney-harbour/book?event=${p.eventKey}`} className={`mt-2 group flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all ${p.highlight ? "text-[#0A0A0A]" : "text-white ring-1 ring-white/25 hover:ring-amber-300/50 hover:text-amber-300"}`} style={p.highlight ? { background: `linear-gradient(135deg, ${SUNSET} 0%, ${GOLD} 100%)`, boxShadow: "0 15px 40px -10px rgba(232,181,58,0.6)" } : {}} data-testid={`aus-day-pkg-cta-${i}`}>
           {p.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
         )}
        </div>
       );
      })}
     </div>
     <div className="mt-10 text-center text-white/55 text-xs uppercase tracking-[0.22em] font-bold">Group of 10+? <a href="#enquiry" className="underline" style={{ color: GOLD }}>Enquire about deposit option</a></div>
    </div>
   </section>

   {/* ═══════════ MENU ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0A1417] via-[#0F1B20] to-[#0A1417] py-24 lg:py-32 overflow-hidden isolate" data-testid="aus-day-menu">
    <Starfield density={30} color={SUNSET} glow="rgba(255,204,112,0.4)" fixed={false} intensity={0.4} />
    <div className="absolute -top-32 right-1/4 w-[30rem] h-[30rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: "rgba(232,181,58,0.08)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-10">
      <GoldEyebrow>The Australia Day menu · what you'll eat &amp; drink</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Eat, drink &amp; <em className="font-light italic" style={{ color: GOLD }}>celebrate.</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">Standing canapés + mini buffet · identical on day and night sessions. Soft drinks are included, alcoholic drinks are <strong className="text-white">available to purchase</strong> at the licensed bar.</p>
     </div>
     {/* Included vs Available-to-Purchase callout */}
     <div className="grid sm:grid-cols-2 gap-3 mb-10 max-w-3xl mx-auto" data-testid="aus-day-cost-callout">
      <div className="rounded-2xl p-5 ring-1 ring-emerald-400/30 bg-emerald-500/[0.08] flex items-start gap-3">
       <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2.5} />
       <div>
        <div className="text-emerald-300 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Included in your ticket</div>
        <div className="text-white text-sm font-bold leading-[1.55]">4hr cruise · Canapés on arrival · Mini buffet · Unlimited soft drinks</div>
       </div>
      </div>
      <div className="rounded-2xl p-5 ring-1 ring-amber-400/30 bg-amber-500/[0.08] flex items-start gap-3">
       <Sparkles className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
       <div>
        <div className="text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Available to purchase · extra</div>
        <div className="text-white text-sm font-bold leading-[1.55]">Beer · Wine · Spirits · Cocktails (pay at the licensed bar · no BYO alcohol)</div>
       </div>
      </div>
     </div>
     <div className="grid lg:grid-cols-3 gap-6">
      {MENU_BLOCKS.map((m, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4">
         <m.Icon className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.5} />
         <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.18em] font-black ${m.badgeIncluded ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30" : "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/30"}`}>{m.badge}</span>
        </div>
        <h3 className="font-black text-white text-xl mb-1">{m.label}</h3>
        <p className="text-white/55 text-xs uppercase tracking-widest font-bold mb-5">{m.caption}</p>
        <ul className="space-y-3">
         {m.items.map((it, j) => (
          <li key={j} className="text-white/80 text-sm leading-[1.6] flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: GOLD }} />{it}</li>
         ))}
        </ul>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ TIMELINE (Day + Night) ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0E1B1F] via-[#16292F] to-[#0E1B1F] py-24 lg:py-32 overflow-hidden isolate" data-testid="aus-day-timeline">
    <Starfield density={45} color={SUNSET} glow="rgba(255,204,112,0.5)" fixed={false} intensity={0.55} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-14">
      <GoldEyebrow>The run sheet</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       How the day <em className="italic font-light" style={{ color: GOLD }}>(and night)</em> unfolds.
      </h2>
     </div>
     <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
      <TimelineList title="Day Session" subtitle="10:45am, 2:45pm · Regatta + Ferrython" items={TIMELINE_DAY} />
      <TimelineList title="Night Session" subtitle="6:30pm, 10:30pm · Australia Day fireworks" items={TIMELINE_NIGHT} highlight />
     </div>
    </div>
   </section>

   {/* ═══════════ REVIEWS ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0A1417] via-[#0F1B20] to-[#0A1417] py-24 lg:py-32 border-y border-amber-300/10 overflow-hidden isolate" data-testid="aus-day-reviews">
    <Starfield density={28} color={SUNSET} glow="rgba(255,204,112,0.45)" fixed={false} intensity={0.4} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <GoldEyebrow>Guest reviews · 4.9 / 5</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       Australia Day, <em className="italic font-light" style={{ color: GOLD }}>done right.</em>
      </h2>
     </div>
     <div className="grid md:grid-cols-3 gap-6">
      {REVIEWS.map((r, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-7">
        <div className="flex items-center gap-1 mb-4">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" style={{ color: GOLD }} />)}</div>
        <p className="text-white/85 text-base leading-[1.7] italic mb-5">"{r.body}"</p>
        <div className="text-white font-black text-sm">{r.name}</div>
        <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold">{r.role}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ FAQ ═══════════ */}
   <section className="relative bg-gradient-to-b from-[#0A1417] via-[#0F1B20] to-[#0A1417] py-24 lg:py-32 overflow-hidden isolate" data-testid="aus-day-faq">
    <Starfield density={18} color={SUNSET} glow="rgba(255,204,112,0.35)" fixed={false} intensity={0.35} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center mb-12"><GoldEyebrow>Frequently asked</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
       Questions, <em className="italic font-light" style={{ color: GOLD }}>answered.</em>
      </h2>
     </div>
     <div className="space-y-3">
      {FAQ.map((f, i) => (
       <details key={i} className="group rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-5 lg:p-6 cursor-pointer">
        <summary className="flex items-center justify-between gap-4 font-black text-white text-base lg:text-lg list-none">
         <span>{f.q}</span>
         <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-open:rotate-90" style={{ color: GOLD }} />
        </summary>
        <p className="text-white/72 text-base leading-[1.75] mt-4">{f.a}</p>
       </details>
      ))}
     </div>
    </div>
   </section>

   {/* ═══════════ FINAL CTA ═══════════ */}
   <section id="enquiry" className="relative overflow-hidden isolate" data-testid="aus-day-final-cta">
    <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zd1xz4zn_AUSTRALIA-DAY-CRUISES-SYDNEY-HARBOUR.jpg" alt="Australia Day Sydney Harbour" className="absolute inset-0 w-full h-full object-cover scale-110" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1417]/95 via-[#16292F]/75 to-[#16292F]/45" />
    <Starfield density={50} color={SUNSET} glow="rgba(255,204,112,0.55)" fixed={false} intensity={0.55} />
    <div className="absolute -top-32 right-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDuration: "6s", backgroundColor: "rgba(232,181,58,0.18)" }} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10 py-24 lg:py-36">
     <div className="text-center">
      <GoldEyebrow>Australia Day 2027 · selling fast</GoldEyebrow>
      <h2 className="font-black tracking-tight text-white mt-7 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.25rem, 5.5vw, 5rem)" }}>
       See you on <em className="italic font-light" style={{ color: GOLD }}>the harbour.</em>
      </h2>
      <p className="text-white/82 text-base lg:text-lg leading-[1.7] mt-6 max-w-2xl mx-auto">Pick day or night. Tickets emailed instantly. Sells out every year.</p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
       <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-day" className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${SUNSET} 0%, ${GOLD} 100%)`, boxShadow: "0 20px 50px -10px rgba(232,181,58,0.6)" }} data-testid="aus-day-final-day">
        <Sun className="w-4 h-4" /> Buy Day Tickets
       </Link>
       <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-night" className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-amber-300/60 hover:text-amber-300 transition-all text-white" data-testid="aus-day-final-night">
        <Sparkles className="w-4 h-4" /> Buy Night Tickets
       </Link>
       <a href={`tel:${CONTACT.phone1}`} className="group inline-flex items-center gap-2.5 text-white/85 hover:text-white px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/15 hover:border-white/40 backdrop-blur transition-all">
        <Phone className="w-4 h-4" /> {CONTACT.phone1}
       </a>
      </div>
     </div>
    </div>
   </section>

   {/* Sticky mobile CTA */}
   <div className="fixed bottom-4 left-4 right-4 lg:hidden z-40">
    <div className="rounded-2xl p-3 backdrop-blur-xl ring-1 ring-amber-300/30 shadow-[0_15px_40px_-10px_rgba(232,181,58,0.4)]" style={{ backgroundColor: "rgba(10,20,23,0.85)" }}>
     <div className="flex items-center gap-2">
      <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-day" className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-[#0A0A0A] font-black text-xs tracking-[0.12em] uppercase" style={{ background: `linear-gradient(135deg, ${SUNSET} 0%, ${GOLD} 100%)` }} data-testid="aus-day-sticky-day">
       <Sun className="w-3.5 h-3.5" /> Day
      </Link>
      <Link to="/cruises/australia-day-cruises-sydney-harbour/book?event=aus-day-2026-night" className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-white font-black text-xs tracking-[0.12em] uppercase border border-amber-300/50" data-testid="aus-day-sticky-night">
       <Sparkles className="w-3.5 h-3.5" /> Night
      </Link>
     </div>
    </div>
   </div>
  </main>
 );
}

function GoldEyebrow({ children }) {
 return (
  <div className="inline-flex items-center gap-3 text-amber-300 text-[10px] uppercase tracking-[0.42em] font-black">
   <span className="block w-6 h-px bg-amber-300/50" />
   <span style={{ color: GOLD }}>{children}</span>
   <span className="block w-6 h-px bg-amber-300/50" />
  </div>
 );
}

function TimelineList({ title, subtitle, items, highlight }) {
 return (
  <div className={`rounded-3xl ring-1 p-6 lg:p-8 ${highlight ? "" : ""}`} style={{ background: highlight ? `linear-gradient(135deg, rgba(232,181,58,0.10), rgba(14,27,31,0.7))` : "rgba(20,32,38,0.5)", borderColor: highlight ? `${GOLD}55` : "rgba(255,255,255,0.10)" }}>
   <div className="mb-7">
    <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-2" style={{ color: GOLD }}>{subtitle}</div>
    <h3 className="font-black text-white text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
   </div>
   <ol className="relative space-y-6 border-l border-amber-300/20 pl-6">
    {items.map((t, i) => (
     <li key={i} className="relative">
      <span className="absolute -left-[31px] top-1.5 w-3.5 h-3.5 rounded-full ring-4 ring-[#0E1B1F]" style={{ backgroundColor: GOLD }} />
      <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: GOLD }}>{t.time}</div>
      <div className="text-white font-black text-base mt-1">{t.title}</div>
      <div className="text-white/70 text-sm mt-1.5 leading-[1.65]">{t.body}</div>
     </li>
    ))}
   </ol>
  </div>
 );
}
